import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { HERO_POOL, POOL_FULL, villas } from "@/data/villas";
import { ArrowRight, ShieldCheck, Sparkles, Users, MapPin } from "lucide-react";
import SEO from "@/components/SEO";

export default function Home() {
  const { t } = useLang();
  const hasna = villas.hasna;

  return (
    <>
      <SEO pageKey="home" />
      {/* HERO */}
      <section data-testid="hero" className="relative -mt-20 h-[100svh] w-full overflow-hidden">
        <img src={POOL_FULL} alt="Piscine 25 mètres Villa Hasna" className="absolute inset-0 h-full w-full object-cover kenburns" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/60" />
        <div className="relative z-10 h-full dh-container flex flex-col justify-end pb-24 md:pb-32 text-white">
          <div className="fade-up max-w-3xl">
            <div className="dh-overline text-[color:hsl(var(--dh-ocre))] mb-4">
              {t.hero.welcome} · Welcome · مرحبا
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              Dar Hasna
              <span className="block italic text-[color:hsl(var(--dh-ocre))]">Collection</span>
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-white/85 leading-relaxed">{t.hero.subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/villa/hasna" data-testid="hero-cta-discover" className="dh-btn-primary">
                {t.hero.discover} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-availability"
                className="dh-btn-ghost border-white/60 text-white hover:bg-white/10"
              >
                {t.hero.checkAvailability}
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute top-24 end-8 hidden md:flex flex-col items-end text-white/70 text-[10px] tracking-[0.4em] uppercase">
          <span>Palmeraie</span>
          <span>Marrakech</span>
        </div>
      </section>

      {/* LA MAISON — single villa feature */}
      <section data-testid="section-collection" className="dh-container py-24 md:py-32 dh-zellige">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end mb-16">
          <div className="md:col-span-5">
            <div className="dh-overline">{t.collection.overline}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">{t.collection.title}</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-[color:hsl(var(--dh-muted))] text-base leading-relaxed">{t.collection.description}</p>
          </div>
        </div>

        <Link
          to="/villa/hasna"
          data-testid="collection-hasna"
          className="group relative overflow-hidden bg-black block"
        >
          <div className="aspect-[16/9] md:aspect-[21/9] overflow-hidden">
            <img
              src={hasna.hero}
              alt={hasna.name}
              className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-10 md:p-14 text-white">
            <div className="dh-overline text-[color:hsl(var(--dh-ocre))] mb-3">{t.collection[hasna.tagKey]}</div>
            <div className="font-serif text-4xl md:text-6xl">{hasna.name}</div>
            <div className="mt-6 flex items-center gap-2 text-sm uppercase tracking-[0.2em] opacity-90">
              {t.collection.exploreHasna}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </div>
          </div>
        </Link>
      </section>

      {/* WHY */}
      <section data-testid="section-why" className="bg-white border-y border-[color:hsl(var(--dh-ocre)/0.15)]">
        <div className="dh-container py-24 md:py-32">
          <div className="max-w-2xl mb-16">
            <div className="dh-overline">{t.why.overline}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">{t.why.title}</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-10">
            {[
              { icon: ShieldCheck, ...t.why.privacy },
              { icon: Sparkles, ...t.why.luxury },
              { icon: Users, ...t.why.service },
              { icon: MapPin, ...t.why.location },
            ].map((it, i) => (
              <div key={i} data-testid={`why-item-${i}`} className="space-y-3">
                <it.icon className="h-6 w-6 text-[color:hsl(var(--dh-terracotta))]" />
                <div className="font-serif text-2xl">{it.title}</div>
                <p className="text-sm text-[color:hsl(var(--dh-muted))] leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AMBIANCE band */}
      <section className="relative h-[70vh] overflow-hidden">
        <img src={HERO_POOL} alt="Villa Hasna · Palmeraie" className="absolute inset-0 h-full w-full object-cover kenburns" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="relative z-10 h-full dh-container flex items-end pb-16">
          <div className="max-w-xl text-white">
            <div className="dh-overline text-[color:hsl(var(--dh-ocre))]">{t.hero.tagline}</div>
            <Link to="/contact" data-testid="mid-cta-book" className="mt-6 inline-flex dh-btn-primary">
              {t.booking.title} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* POOL — final band */}
      <section data-testid="section-pool" className="relative h-[80vh] overflow-hidden">
        <img src={POOL_FULL} alt="Piscine 25 mètres chauffable" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/50" />
        <div className="relative z-10 h-full dh-container flex flex-col justify-end pb-20 text-white">
          <div className="dh-overline text-[color:hsl(var(--dh-ocre))]">25 mètres · privée · chauffable</div>
          <div className="font-serif text-5xl md:text-7xl mt-4 max-w-3xl leading-[0.95]">
            Une piscine à traverser<br/>
            <span className="italic text-[color:hsl(var(--dh-ocre))]">comme un rêve.</span>
          </div>
        </div>
      </section>
    </>
  );
}
